import React, { useState, useEffect } from 'react';
import { X, Play, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { MCPTool } from '@/types';
import { apiClient } from '@/utils/apiClient';
import { useAppStore } from '@/stores/appStore';
import { runChatTurn } from '@/hooks/useChatService';
import { clsx } from 'clsx';

interface ToolInvocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  tool: MCPTool;
  serverName: string;
  serverId: string;
}

export const ToolInvocationModal: React.FC<ToolInvocationModalProps> = ({
  isOpen,
  onClose,
  tool,
  serverName,
}) => {
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { addMessage, createSession } = useAppStore();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setParameters({});
      setResult(null);
      setError(null);

      // Initialize with default values if provided in schema
      if (tool.inputSchema?.properties) {
        const defaults: Record<string, any> = {};
        Object.entries(tool.inputSchema.properties).forEach(([key, schema]: [string, any]) => {
          if (schema.default !== undefined) {
            defaults[key] = schema.default;
          }
        });
        setParameters(defaults);
      }
    }
  }, [isOpen, tool]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleExecute = async () => {
    setIsExecuting(true);
    setError(null);
    setResult(null);

    // STORY-299: the chat the tool window was opened over is fixed now, before
    // any await. Its "Executed tool" message and the interpretation go to it
    // even if the user switches chats while the tool or the LLM is working.
    let sessionId = useAppStore.getState().currentSession?.id;

    const startTime = Date.now();

    try {
      const response = await apiClient.callTool(serverName, tool.name, parameters);
      const endTime = Date.now();
      const duration = endTime - startTime;

      console.log('Tool execution response:', response);

      // Extract the actual result from the response
      const actualResult = response.result || response;

      setResult(actualResult);

      // Ensure we have an active session - create one if needed
      if (!sessionId) {
        console.log('No active session, creating one for tool results...');
        createSession(`Tool: ${tool.name}`);
        // Wait a bit for session to be created
        await new Promise(resolve => setTimeout(resolve, 150));
        // Re-fetch the current session after creation
        sessionId = useAppStore.getState().currentSession?.id;
      }

      // Format the output nicely
      const outputText = typeof actualResult === 'string'
        ? actualResult
        : JSON.stringify(actualResult, null, 2);

      // Add a user message showing the tool was invoked
      addMessage({
        content: `🔧 Executed tool: **${tool.name}** from *${serverName}*`,
        type: 'user',
        metadata: {
          toolCalls: [{
            name: tool.name,
            args: parameters,
            status: 'ok',
            duration_ms: duration,
            output: outputText
          }]
        }
      }, sessionId);

      // Fallback: show raw result if LLM interpretation fails
      const addRawResult = () => {
        const resultPreview = outputText.length > 500
          ? outputText.substring(0, 500) + '...\n\n(See full result in the expanded tool call above)'
          : outputText;

        addMessage({
          content: `✅ Tool execution completed successfully.\n\n**Result:**\n\`\`\`json\n${resultPreview}\n\`\`\``,
          type: 'assistant',
        }, sessionId);
      };

      // Ask LLM to interpret the result instead of showing raw JSON
      console.log('Requesting LLM interpretation of tool result...');

      // Prepare context for LLM
      const interpretationPrompt = `I just executed the tool "${tool.name}" with the following parameters:

\`\`\`json
${JSON.stringify(parameters, null, 2)}
\`\`\`

The tool returned this result:

\`\`\`json
${outputText.length > 2000 ? outputText.substring(0, 2000) + '\n...(truncated)' : outputText}
\`\`\`

Please analyze and explain this result in a clear, user-friendly way. Focus on:
- Key insights and important information
- Summary of what was found
- Any notable patterns or issues
- Actionable recommendations if applicable

Be concise but thorough. Use tables or lists where appropriate for better readability.`;

      // Send to LLM for interpretation — a turn of the chat, so the chat
      // shows as busy and Stop applies to it (STORY-299).
      const outcome = await runChatTurn({
        sessionId: sessionId ?? '',
        request: { content: interpretationPrompt },
        onResponse: (interpretationResponse) => {
          // Add the LLM's interpretation as assistant message
          addMessage({
            content: interpretationResponse.content,
            type: 'assistant',
          }, sessionId);
          console.log('LLM interpretation added to chat');
        },
        onError: (interpretError) => {
          console.error('Failed to get LLM interpretation:', interpretError);
          addRawResult();
        },
      });
      // The chat already has a turn in flight: one POST /chat per chat at a
      // time, so show the raw result instead of a second request.
      if (outcome === 'busy') addRawResult();

      // Optional: Auto-close modal after successful execution and a brief delay
      // Uncomment the next two lines if you want the modal to auto-close
      // await new Promise(resolve => setTimeout(resolve, 1500));
      // onClose();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to execute tool';
      setError(errorMessage);

      // Ensure we have an active session - create one if needed
      if (!sessionId) {
        console.log('No active session, creating one for error...');
        createSession(`Tool: ${tool.name}`);
        await new Promise(resolve => setTimeout(resolve, 100));
        sessionId = useAppStore.getState().currentSession?.id;
      }

      // Send error to chat
      addMessage({
        content: `🔧 Executed tool: **${tool.name}** from *${serverName}*`,
        type: 'user',
        metadata: {
          toolCalls: [{
            name: tool.name,
            args: parameters,
            status: 'error',
            duration_ms: null,
            output: errorMessage
          }]
        }
      }, sessionId);

      addMessage({
        content: `❌ Tool execution failed: ${errorMessage}`,
        type: 'assistant',
        metadata: {
          error: errorMessage
        }
      }, sessionId);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleParameterChange = (key: string, value: any, type?: string) => {
    let processedValue = value;
    
    // Type conversion
    if (type === 'number' || type === 'integer') {
      processedValue = value === '' ? '' : Number(value);
    } else if (type === 'boolean') {
      processedValue = value === 'true';
    } else if (type === 'array') {
      // Try to parse as JSON array
      try {
        processedValue = JSON.parse(value);
      } catch {
        processedValue = value;
      }
    } else if (type === 'object') {
      // Try to parse as JSON object
      try {
        processedValue = JSON.parse(value);
      } catch {
        processedValue = value;
      }
    }
    
    setParameters(prev => ({ ...prev, [key]: processedValue }));
  };

  const renderParameterInput = (paramName: string, paramSchema: any) => {
    const type = paramSchema.type || 'string';
    const isRequired = tool.inputSchema?.required?.includes(paramName);
    const value = parameters[paramName] ?? '';

    const inputClasses = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500";

    switch (type) {
      case 'boolean':
        return (
          <select
            value={value.toString()}
            onChange={(e) => handleParameterChange(paramName, e.target.value, type)}
            className={inputClasses}
            required={isRequired}
          >
            <option value="">Select...</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        );

      case 'integer':
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleParameterChange(paramName, e.target.value, type)}
            className={inputClasses}
            placeholder={paramSchema.description || `Enter ${paramName}`}
            required={isRequired}
            min={paramSchema.minimum}
            max={paramSchema.maximum}
            step={type === 'integer' ? 1 : 'any'}
          />
        );

      case 'array':
      case 'object':
        return (
          <textarea
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={(e) => handleParameterChange(paramName, e.target.value, type)}
            className={clsx(inputClasses, 'font-mono text-sm')}
            placeholder={type === 'array' ? '["item1", "item2"]' : '{"key": "value"}'}
            rows={4}
            required={isRequired}
          />
        );

      default:
        if (paramSchema.enum) {
          return (
            <select
              value={value}
              onChange={(e) => handleParameterChange(paramName, e.target.value, type)}
              className={inputClasses}
              required={isRequired}
            >
              <option value="">Select...</option>
              {paramSchema.enum.map((option: any) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        }

        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleParameterChange(paramName, e.target.value, type)}
            className={inputClasses}
            placeholder={paramSchema.description || `Enter ${paramName}`}
            required={isRequired}
          />
        );
    }
  };

  if (!isOpen) return null;

  const properties = tool.inputSchema?.properties || {};
  const hasParameters = Object.keys(properties).length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Background overlay */}
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal panel - centered floating window */}
      <div className="relative w-full max-w-3xl max-h-[85vh] overflow-hidden bg-white dark:bg-gray-800 shadow-2xl rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Execute Tool
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {serverName} → <span className="font-mono text-blue-600 dark:text-blue-400">{tool.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-4 overflow-y-auto">
          {/* Tool description */}
          {tool.description && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                {tool.description}
              </p>
            </div>
          )}

          {/* Parameters form */}
          {hasParameters ? (
            <form onSubmit={(e) => { e.preventDefault(); handleExecute(); }} className="space-y-4">
              {Object.entries(properties).map(([paramName, paramSchema]: [string, any]) => {
                const isRequired = tool.inputSchema?.required?.includes(paramName);
                
                return (
                  <div key={paramName} className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                      {paramName}
                      {isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                      <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                        ({paramSchema.type || 'string'})
                      </span>
                    </label>
                    {paramSchema.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                        {paramSchema.description}
                      </p>
                    )}
                    {renderParameterInput(paramName, paramSchema)}
                  </div>
                );
              })}
            </form>
          ) : (
            <div className="text-sm text-gray-600 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-center">
              This tool requires no parameters.
            </div>
          )}

          {/* Result display */}
          {result && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">
                    Execution successful
                  </p>
                  <pre className="text-xs bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-3 rounded border border-green-200 dark:border-green-800 overflow-x-auto font-mono">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
            disabled={isExecuting}
          >
            Close
          </button>
          <button
            onClick={handleExecute}
            disabled={isExecuting}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isExecuting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Executing...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Execute Tool
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


